import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, Profile } from '../lib/supabase';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const INACTIVITY_TIMEOUT = 8 * 60 * 60 * 1000; // 8 horas en milisegundos
const ACTIVITY_CHECK_INTERVAL = 60 * 1000; // Verificar cada minuto

async function sendWelcomeEmail(email: string, userData: Partial<Profile>) {
  try {
    console.log('📧 [AuthContext] Iniciando envío de email de bienvenida...');
    console.log('📧 [AuthContext] Email destino:', email);
    console.log('📧 [AuthContext] Rol:', userData.role);
    
    const emailType = userData.role === 'employer' ? 'welcome_employer' : 'welcome_candidate';
    
    console.log('📧 [AuthContext] Tipo de email:', emailType);
    console.log('📧 [AuthContext] URL de función:', `${SUPABASE_URL}/functions/v1/send-email`);
    
    const response = await fetch(`${SUPABASE_URL}/functions/v1/send-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: email,
        type: emailType,
        data: {
          name: userData.full_name || 'Usuario',
          company_name: userData.company_name || '',
          role: userData.role,
        },
      }),
    });
    
    console.log('📧 [AuthContext] Respuesta HTTP status:', response.status);
    
    const result = await response.json();
    console.log('📧 [AuthContext] Respuesta completa:', JSON.stringify(result, null, 2));
    
    if (result.success) {
      console.log(`✅ [AuthContext] Email de bienvenida enviado exitosamente a ${email}`);
    } else {
      console.error('❌ [AuthContext] Error en respuesta de email:', result.error);
      console.error('❌ [AuthContext] Detalles:', result.details);
    }
    
    return result;
  } catch (err) {
    console.error('❌ [AuthContext] Excepción al enviar email de bienvenida:', err);
    console.error('❌ [AuthContext] Stack trace:', err instanceof Error ? err.stack : 'No stack');
    throw err; // Re-lanzar para que se vea el error
  }
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, userData: Partial<Profile>) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (data: Partial<Profile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const profileLoadedRef = useRef<((profile: Profile | null) => void) | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const inactivityTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // --- Inactivity timeout logic ---
  const updateLastActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const checkInactivity = useCallback(() => {
    if (!user) return;
    const elapsed = Date.now() - lastActivityRef.current;
    if (elapsed >= INACTIVITY_TIMEOUT) {
      // Auto logout por inactividad
      console.log('⏰ Sesión cerrada por inactividad (8 horas)');
      clearSession();
      supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      window.location.href = '/auth/login?reason=inactivity';
    }
  }, [user]);

  useEffect(() => {
    // Registrar eventos de actividad del usuario
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    const handler = () => updateLastActivity();

    events.forEach(evt => window.addEventListener(evt, handler, { passive: true }));

    // Verificar inactividad periódicamente
    inactivityTimerRef.current = setInterval(checkInactivity, ACTIVITY_CHECK_INTERVAL);

    return () => {
      events.forEach(evt => window.removeEventListener(evt, handler));
      if (inactivityTimerRef.current) {
        clearInterval(inactivityTimerRef.current);
      }
    };
  }, [updateLastActivity, checkInactivity]);
  // --- End inactivity timeout logic ---

  const loadProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      setProfile(data);
      if (profileLoadedRef.current) {
        profileLoadedRef.current(data);
        profileLoadedRef.current = null;
      }
      return data;
    } catch (error) {
      console.error('Error loading profile:', error);
      if (profileLoadedRef.current) {
        profileLoadedRef.current(null);
        profileLoadedRef.current = null;
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearSession = useCallback(() => {
    setUser(null);
    setProfile(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('Error getting session:', error);
        clearSession();
        return;
      }
      
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'TOKEN_REFRESHED' && !session) {
        console.warn('Token refresh failed, clearing session');
        clearSession();
        return;
      }

      if (event === 'SIGNED_OUT') {
        clearSession();
        return;
      }

      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (currentUser) {
        loadProfile(currentUser.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [loadProfile, clearSession]);

  const signUp = async (email: string, password: string, userData: any) => {
    try {
      const redirectUrl = `${window.location.origin}/auth/login`;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo: redirectUrl,
        },
      });

      if (error) throw error;
      if (!data.user) throw new Error('No se pudo crear el usuario');

      // Enviar email de bienvenida (no bloquear si falla)
      try {
        const emailType = userData.role === 'candidate' ? 'welcome_candidate' : 'welcome_employer';
        await supabase.functions.invoke('send-email', {
          body: {
            to: email,
            type: emailType,
            data: {
              name: userData.full_name || 'Usuario',
              company_name: userData.company_name || '',
              role: userData.role,
            },
          },
        });
      } catch (emailError) {
        console.error('Error al enviar email:', emailError);
      }
    } catch (error) {
      console.error('Error en signUp:', error);
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        throw new Error('Correo o contraseña incorrectos');
      }
      if (error.message.includes('Email not confirmed')) {
        throw new Error('Debes confirmar tu correo electrónico antes de iniciar sesión. Revisa tu bandeja de entrada.');
      }
      throw error;
    }

    if (!data.user) {
      throw new Error('No se pudo iniciar sesión');
    }

    // Resetear timer de inactividad al hacer login
    lastActivityRef.current = Date.now();

    // Esperar a que se cargue el perfil con timeout más corto
    await new Promise<Profile | null>((resolve) => {
      profileLoadedRef.current = resolve;
      loadProfile(data.user.id);
      setTimeout(() => {
        if (profileLoadedRef.current) {
          profileLoadedRef.current(null);
          profileLoadedRef.current = null;
        }
        resolve(null);
      }, 3000);
    });
  };

  const signOut = async () => {
    try {
      // Primero limpiar el estado local inmediatamente
      clearSession();
      
      // Luego hacer signOut de Supabase en segundo plano
      await supabase.auth.signOut({ scope: 'local' });
      
      // Redirigir inmediatamente
      window.location.href = '/';
    } catch (error) {
      console.error('Error en signOut:', error);
      // Asegurar que se limpie el estado incluso si hay error
      clearSession();
      window.location.href = '/';
    }
  };

  const updateProfile = async (data: Partial<Profile>) => {
    if (!user) throw new Error('No hay usuario autenticado');

    const { error } = await supabase
      .from('profiles')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', user.id);

    if (error) throw error;
    await loadProfile(user.id);
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, signUp, signIn, signOut, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
}
